import {
    ApiMetadata, ApiMetadataCacheAccessor, ArrayUtil, AuthToken, ClientRegistration, Collection, DynamicLoader,
    GeneralSettings, LogManager, McLeodContext, ModelRow, Navigation, RegisteredRoutes, SettingsLoader, StringUtil,
    UrlUtil, ValueOrFunction, ValueResolver, WindowTitle
} from "@mcleod/core";
import {
    AllowedToSearch, Component, CrudDecorator, DecoratorFactory, IFrame, Layout, LayoutCacheAccessor, LayoutProps,
    Overlay, Panel, PanelStaticError, ScreenStack, Spinner
} from "@mcleod/components";
import { DataLoadActionHandler } from "../DataLoadActionHandler";
import { MainPage } from "../mainpage/MainPage";
import { RouterProps } from "./RouterProps";
import { TempLicensingToast } from "../TempLicensingToast";

const log = LogManager.getLogger("common.Router");

export interface RouteChangedEvent {
    source: Router;
    route: string;
    layout: Layout;
}

export interface ChildRouteConfig {
    routeChangeListener: RouteChangeListener;
    layoutPropsSupplier: ValueOrFunction<Partial<LayoutProps>>;
}

export interface RouteChangeListener {
    (event: RouteChangedEvent): void;
}

/* This call is how we inform the History module that the main page's Router is
 * what will handle be used to display new content as the user navigates the site */
Navigation.setNavigationHandler({
    displayRoute: (path: string, props: any) => {
        const mainPage = MainPage.instance;
        if (mainPage == null)
            window.location.pathname = path;
        else {
            // window.history.pushState(props, "McLeod", path);
            if (mainPage.router == null) {
                path = DynamicLoader.getRouteFromURL(path);
                const cls = DynamicLoader.getClassForPath(path);
                const inst = new cls(props);
                mainPage.removeAll();
                mainPage.add(inst);
                return inst;
            }
            else
                return Router.instance.displayRoute(path, props);
        }
    },
    getRoute: () => {
        const router = Router.instance;
        let result = router._route;
        if (!StringUtil.isEmptyString(router._routeSearchString))
            result += router._routeSearchString;
        return result;
    }
});

/**
 * The Router class is responsible for figuring out which Layout to display based on the user's
 * current browser address.  It dynamically loads the class specified by the address and
 */
export abstract class Router extends Panel {
    private static _instance: Router;
    _isMainRouter: boolean;
    _route: string;
    _routeSearchString: string;
    private _routeChangeListeners: RouteChangeListener[] = [];
    private _childRouteConfigs: Map<string, ChildRouteConfig> = new Map();

    protected constructor(props: Partial<RouterProps>) {
        super({
            id: props?.id == null ? "mainRouter" : props.id,
            fillRow: true,
            fillHeight: true,
            padding: 24,
            paddingRight: 18,
            scrollX: true,
            scrollY: true,
            ...props
        });

        // do not add non-root Routers to the ScreenStack; they should be contained
        // in some other panel/layout that is being displayed within the root Router.
        if (props?.isMainDocumentRouter !== false) {
            ScreenStack.push(this);
            this._isMainRouter = true;
            McLeodContext.setRouter(this);
            Router._instance = this;
        }
        else
            this._isMainRouter = false;
    }

    public displayRoute(route: string, props?: any): Layout {
        return this._populateAuthSettingsThenDisplay(route, props);
    }

    private _populateAuthSettingsThenDisplay(route: string, props?: any): Layout {
        if (props?._auth != null) {
            AuthToken.set(props._auth);
            delete props._auth;
            window.history.replaceState({ props }, "McLeod", window.location.pathname);
        }
        if (AuthToken.isAuthenticated() && GeneralSettings.getSingleton().authSettingsPopulated() === false) {
            ClientRegistration.addUnregisterBeforeUnloadListener();
            SettingsLoader.loadAuthSettings(
                this.beforeSettingsApply,
                (response: any, appliedSettings: boolean) => {
                    this.afterSettingsApply(response, appliedSettings, route, props);
                },
                this.handleSettingsError
            );
            return null;
        }
        else {
            return this._displayRoute(route, props);
        }
    }

    private beforeSettingsApply = (response: any): boolean => {
        return ClientRegistration.handleLicensingResult(response);
    }

    private afterSettingsApply(_response: any, appliedSettings: boolean, route: string, props?: any) {
        if (appliedSettings === true) {
            this._displayRoute(route, props);
            TempLicensingToast.display();
        }
    }

    private handleSettingsError = (error: any) => {
        log.info("%cError loading page\n", "background: #222; color: white", error);
        const errorSummary = "Sorry, there was an error loading this page.";
        const devDetail = error + "\n";
        this.setRouterContents(new PanelStaticError(errorSummary, null, devDetail));
    }

    private _displayRoute(route: string, props?: any): Layout {
        if (route?.indexOf(":") >= 0) {
            this.displayRemoteRoute(route, props);
            return null;
        }

        Overlay.hideAllOverlays();
        this.getKeyHandlerGroup().clear();
        const parentRouter = this.getParentRouter();
        this.copyCachesFromParentRouter(parentRouter);
        const routeConfig = this.getRouteConfigFromParentRouter(parentRouter);
        Navigation.removeAllNavigationListeners();
        props = {
            ...props,
            ...UrlUtil.getPropsFromUrl(route),
            ...ValueResolver.resolve(routeConfig?.layoutPropsSupplier)
        };
        route = DynamicLoader.getRouteFromURL(route);
        this.dismissAllPopups();
        AllowedToSearch.clear();
        this.removeAll();
        const auth = AuthToken.isAuthenticated();
        const base = GeneralSettings.get().base_url_offset;
        if (base != null && route.startsWith(base)) {
            route = route.substring(base.length);
            if (route.startsWith("/"))
                route = route.substring(1);
        }
        if (route.length === 0 || route === "index.html") {
            route = auth ? RegisteredRoutes.homeRoute : RegisteredRoutes.loginRoute;
        }
        this._route = route;
        this._routeSearchString = window.location.search;
        if (props._undecorated !== "")
            MainPage.instance.pageHeader.syncNavigation();

        this.add(new Spinner());
        const layout = Layout.getLayout(route, props);
        if (props._undecorated !== "")
            this.applyRouterPropsFromPage(layout);
        layout.addLayoutLoadListener(() => {
            const decorated: Component = DecoratorFactory.decoratePage(layout, this._routeSearchString);
            this.setRouterContents(decorated);
            if (this._isMainRouter == true) {
                //if the layout in the main router is changing, the router needs to fill its key handler group
                //this pulls all the key handlers up into a flat collection within the router
                this.fillKeyHandlerGroup(true);

                // CrudDecorator manages its own titles.
                if (!(decorated instanceof CrudDecorator))
                    WindowTitle.resetWith(() => layout.windowTitle, "Router");
            }
            else {
                // if the layout in a nested router (such as the demo router) is changing, the main router needs
                // to fill its key handler group.  key handlers from the nested router should be pulled up
                // into the main router's flat collection
                Router.instance.fillKeyHandlerGroup(true);
            }
        });
        layout.addLoadErrorListener(() => {
            WindowTitle.resetWith(null, "Router (Load Error)");
            this.setRouterContents(layout);
        });

        const dataLoadActionProps = DataLoadActionHandler.gatherFromUrlProps(props);
        if (ArrayUtil.isEmptyArray(dataLoadActionProps) === false) {
            layout.addLayoutDataLoadListener(() => DataLoadActionHandler.handleDataLoadActions(layout, dataLoadActionProps, props));
        }

        this.fireRouteChangeEvents(this._route, layout, routeConfig?.routeChangeListener);
        return layout;
    }

    private displayRemoteRoute(route: string, props: any) {
        if (route.startsWith("/"))
            route = route.substring(1);
        const iframe = new IFrame({src: route, fillRow: true, fillHeight: true, ...props});
        this.setRouterContents(iframe);
    }

    redisplayCurrentRoute() {
        this.displayRoute(
            window.location.pathname,
            UrlUtil.getPropsFromUrl(window.location.search)
        );
    }

    private setRouterContents(...components: Component[]) {
        this.removeAll();
        this.add(...components);
    }

    private applyRouterPropsFromPage(owner): void {
        if (owner == null || owner.getRouterProps == null)
            this.setProps({ padding: this.defaultRouterPadding });
        else
            this.setProps(owner.getRouterProps());
    }

    public get defaultRouterPadding(): number {
        return 24;
    }

    private fireRouteChangeEvents(route: string, layout: Layout, parentRouteChangeListener: RouteChangeListener): void {
        const fireEvent = (listener: RouteChangeListener) => {
            try {
                listener?.({ source: this, route, layout });
            } catch (e) {
                log.error("Error in route change listener:", e);
            }
        };
        this._routeChangeListeners?.forEach(fireEvent);
        fireEvent(parentRouteChangeListener);
    }

    private getParentRouter(): Router {
        if (window.opener && !window.opener.closed) {
            try {
                const openerOrigin = window.opener.location.origin;
                if (openerOrigin === window.location.origin) {
                    return window.opener["mcleodContext"]?.router as Router;
                }
            } catch (error) {
                log.error("Error accessing parent window", error);
            }
        }
        return null;
    }

    public addRouteChangeListener(listener: RouteChangeListener) {
        this._routeChangeListeners.push(listener);
    }

    public removeRouteChangeListener(listener: RouteChangeListener) {
        this._routeChangeListeners = this._routeChangeListeners.filter(l => l !== listener);
    }

    public static isDisplayingHomePage(): boolean {
        return Router.instance._route == RegisteredRoutes.homeRoute;
    }

    public static get instance(): Router {
        return this._instance;
    }

    public addChildRouteConfig(key: string, config: ChildRouteConfig) {
        this._childRouteConfigs.set(key, config);
    }

    public removeChildRouteConfig(key: string) {
        this._childRouteConfigs.delete(key);
    }

    private getRouteConfigFromParentRouter(parentRouter: Router): ChildRouteConfig {
        const configs = parentRouter?._childRouteConfigs;
        if (configs == null)
            return null;
        const value = configs.get(window.name);
        configs.delete(window.name);
        return value;
    }

    private copyCachesFromParentRouter(parentRouter: Router) {
        if (parentRouter == null) {
            return;
        }
        if (LayoutCacheAccessor.isEmpty()) {
            LayoutCacheAccessor.putAll(parentRouter.getLayoutCacheCopy());
        }
        if (ApiMetadataCacheAccessor.isEmpty()) {
            ApiMetadataCacheAccessor.putAll(parentRouter.getApiMetaDataCacheCopy());
        }
    }

    getLayoutCacheCopy(): Collection<ModelRow> {
        return LayoutCacheAccessor.getCopy();
    }

    getApiMetaDataCacheCopy(): Collection<ApiMetadata> {
        return ApiMetadataCacheAccessor.getCopy();
    }
}
